import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.tsx';
import { createConsoleQueryClient } from './api/queryClient.ts';
import { I18nProvider } from './i18n/index.ts';
// Self-hosted fonts (epic #950: no text-font CDN requests). Fontsource
// ships unicode-range-sliced woff2 per weight — browsers fetch only the
// slices a page actually renders. Newsreader is display-size serif only
// (DESIGN.md §3); Geist Sans/Mono carry everything else.
import '@fontsource/geist-sans/400.css';
import '@fontsource/geist-sans/500.css';
import '@fontsource/geist-sans/600.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/newsreader/400.css';
import '@fontsource/newsreader/500.css';
import './styles.css';

/**
 * Browser entry — epic #950 PR-8 inlines the former console-shared
 * `mountConsoleApp` bootstrap. Keep this file tiny: all app composition
 * lives in `App.tsx`. A single `QueryClient` instance wraps the entire
 * app; tests mount their own via `test/rig.tsx`.
 */
const rootEl = document.getElementById('root');
if (rootEl === null) {
  throw new Error('nxtpath-console-web: #root element missing from index.html');
}

const queryClient = createConsoleQueryClient();

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <I18nProvider>
          <App />
        </I18nProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
